import { useOnboardingStore } from '@/store/onboardingStore';
import { TextureButton } from '@/components/ui/TextureButton';
import { Check, Brain, Flame, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export function ProjectionsScreen() {
    const { complete, profile } = useOnboardingStore();
    const navigate = useNavigate();

    const [calculating, setCalculating] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setCalculating(false), 2400);
        return () => clearTimeout(timer);
    }, []);

    const handleFinish = async () => {
        await complete();
        navigate('/');
    };

    if (calculating) {
        return (
            <div className="flex flex-col items-center justify-center h-[60vh] animate-fade-in space-y-8 px-8 text-center">
                <div className="relative w-24 h-24">
                    <Brain className="w-full h-full text-blue-500/20 absolute inset-0 animate-pulse" />
                    <Brain className="w-full h-full text-blue-400 absolute inset-0 animate-[clip_1s_ease-in-out_infinite]" style={{ clipPath: 'inset(0 0 50% 0)' }} />

                    {/* Floating Particles */}
                    <div className="absolute -top-4 -right-4 w-3 h-3 bg-blue-400 rounded-full animate-bounce delay-100" />
                    <div className="absolute -bottom-2 -left-4 w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-300" />
                </div>

                <div>
                    <h3 className="text-lg font-bold text-white mb-2">Analyzing Training Data...</h3>
                    <div className="h-1.5 w-48 bg-blue-900/30 rounded-full overflow-hidden mx-auto">
                        <div className="h-full bg-blue-500 animate-[indeterminate_1.5s_infinite_linear] w-1/3 rounded-full" />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-[10px] text-blue-400 font-mono opacity-80">
                    <span className="animate-pulse">WEIGHT_VELOCITY</span>
                    <span className="animate-pulse delay-75">TECHNIQUE_IQ</span>
                    <span className="animate-pulse delay-150">CARDIO_BASE</span>
                </div>
            </div>
        );
    }

    return (
        <div className="px-2 animate-fade-in-right space-y-6 pb-8">
            <h2 className="text-xl font-bold text-white uppercase tracking-tight">Your Goals</h2>

            <div className="flex items-center gap-2 mb-2">
                <div className="bg-blue-500/20 p-1 rounded">
                    <Brain className="w-3 h-3 text-blue-400" />
                </div>
                <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">AI Analysis Complete</span>
            </div>

            {/* Growth Chart */}
            <div className="bg-surface-card border border-white/10 rounded-2xl p-5 relative overflow-hidden">
                <h3 className="font-bold text-white mb-4">Projected Growth Over 6 Months</h3>

                <div className="flex items-stretch justify-between h-32 gap-2 mt-4 px-2">
                    {/* Simulated Bars */}
                    {[1, 2, 3, 4, 5, 6].map((mo, i) => {
                        const h1 = 30 + (i * 12);
                        const h2 = 25 + (i * 2);
                        return (
                            <div key={mo} className="flex flex-col items-center gap-1 w-full relative group">
                                <div className="flex gap-1 w-full justify-center items-end flex-1">
                                    <div
                                        className="w-2 bg-green-500 rounded-t-sm shadow-[0_0_8px_rgba(34,197,94,0.4)] animate-[growth_1s_ease-out]"
                                        style={{ height: `${h1}%`, animationDelay: `${i * 100}ms` }}
                                    />
                                    <div
                                        className="w-2 bg-red-500/50 rounded-t-sm"
                                        style={{ height: `${h2}%` }}
                                    />
                                </div>
                                <span className="text-[8px] text-text-muted mt-1">Mo {mo}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="flex gap-4 text-[9px] font-bold uppercase mt-4">
                    <div className="flex items-center gap-1 text-green-400">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        With Wrestle AI
                    </div>
                    <div className="flex items-center gap-1 text-red-400">
                        <div className="w-2 h-2 rounded-full bg-red-500" />
                        Without
                    </div>
                </div>
            </div>

            <div className="text-sm text-text-secondary leading-relaxed">
                Based on your profile as a <strong className="text-white">{profile.matIdentity?.replace('_', ' ') || 'Wrestler'}</strong>,
                here's how Wrestle AI accelerates your journey:
            </div>

            {/* Success List */}
            <div className="space-y-3">
                {[
                    'Analyze your technique through AI video breakdowns',
                    `Build custom drilling sequences for your ${profile.topAttack?.replace('_', ' ') || 'attacks'}`,
                    `Monitor your weight cut to ${profile.weightClass || 'target'} lbs with daily tracking`,
                    'Optimize nutrition for training intensity'
                ].map((item, i) => (
                    <div key={i} className="flex gap-3 text-sm text-text-secondary">
                        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        <span>{item}</span>
                    </div>
                ))}
            </div>

            {/* Profile Ready Checklist */}
            <div className="mt-8 space-y-2">
                <div className="bg-surface-card border border-white/10 p-4 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400">
                            <Brain className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-bold text-white text-sm">AI Coaching Profile</div>
                            <div className="text-[10px] text-text-muted">Custom-built for your style</div>
                        </div>
                    </div>
                    <Check className="w-4 h-4 text-blue-400" />
                </div>

                <div className="bg-surface-card border border-white/10 p-4 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400">
                            <Flame className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-bold text-white text-sm">Nutrition Plan</div>
                            <div className="text-[10px] text-text-muted">Calibrated to your weight class</div>
                        </div>
                    </div>
                    <Check className="w-4 h-4 text-blue-400" />
                </div>

                <div className="bg-surface-card border border-white/10 p-4 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                            <Target className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-bold text-white text-sm">Training Roadmap</div>
                            <div className="text-[10px] text-text-muted">Personalized drill sequences</div>
                        </div>
                    </div>
                    <Check className="w-4 h-4 text-blue-400" />
                </div>
            </div>

            <TextureButton onClick={handleFinish} className="w-full mt-8">
                Enter Dashboard
            </TextureButton>
        </div>
    );
}
